/**
 * Módulo Fuentes: lista en cápsulas, conexión compacta, recorte y mapa de uniones.
 */
import React, { useState } from 'react';
import { ink, studio } from '../studioUi';
import { DATE_ORDER_OPTIONS, normalizeDateOrder, parseSpreadsheetInput, sourceLabel } from './engineConfig';
import SourceFiltersPanel from './SourceFiltersPanel';
import SourceColumnPicker from './SourceColumnPicker';
import SourceFieldsModal from './SourceFieldsModal';
import JoinBlendCanvas from './JoinBlendCanvas';

export default function DataSourcesStudio({
    dataSources = [],
    joins = [],
    schemasBySource = {},
    activeSourceId,
    activeSource,
    onSelectSource,
    onAddSource,
    onAddJoin,
    onRemoveSource,
    onRemoveJoin,
    onClearJoins,
    onPatchSource,
    onClose,
    spreadsheetInput,
    spreadsheetId,
    sheetName,
    availableSheets = [],
    onSheetChange,
    onLoadWorksheets,
    loadingSheets,
    loadingColumns,
    rowCount,
    schemaColumns = [],
    onOpenDrive,
    onSourceFiltersChange,
    onSourceColumnsChange,
    onBlend,
    onPatchJoin,
    onTestJoin,
    onOpenJoinMap,
    sourceInUse = false,
    onReplaceSource,
}) {
    const [fieldsOpen, setFieldsOpen] = useState(false);
    const schema = schemasBySource[activeSource?.id] || {};
    const cols = schema.columns?.length || schemaColumns.length || 0;
    const rows = schema.rowCount != null ? schema.rowCount : rowCount;
    const waiting = activeSource?.kind !== 'join' && activeSource?.spreadsheet_id && !cols;
    /* Con elementos que usan la fuente, un enlace distinto pasa por «Reemplazar fuente». */
    const connect = () => {
        const parsed = parseSpreadsheetInput(spreadsheetInput);
        if (sourceInUse && onReplaceSource && parsed.spreadsheet_id && parsed.spreadsheet_id !== spreadsheetId) {
            onReplaceSource(activeSource.id, spreadsheetInput);
            return;
        }
        onLoadWorksheets();
    };

    return (
        <section style={ui.shell}>
            <header style={ui.header}>
                <div>
                    <p style={studio.kicker}>Fuentes de datos</p>
                    <p style={ui.sub}>Hojas, recorte al cargar, campos incluidos y uniones. Los filtros del informe van aparte, en la barra.</p>
                </div>
                <div style={ui.headerActions}>
                    <button type="button" style={ui.pillGhost} onClick={onAddSource}>+ Hoja</button>
                    <button type="button" style={ui.pillGhost} onClick={onAddJoin}>+ Unión</button>
                    <button type="button" style={ui.pillGhost} onClick={onOpenJoinMap}>Mapa</button>
                    <button
                        type="button"
                        style={ui.pillGhost}
                        disabled={!activeSource}
                        onClick={() => setFieldsOpen(true)}
                    >
                        Campos
                    </button>
                    {joins.length > 0 && (
                        <button type="button" style={ui.pillDangerHead} onClick={onClearJoins}>Limpiar uniones</button>
                    )}
                    <button type="button" style={ui.pillQuiet} onClick={onClose}>Cerrar</button>
                </div>
            </header>

            <div style={ui.body}>
                <aside style={ui.rail}>
                    <span style={ui.railLabel}>Catálogo</span>
                    {dataSources.map((source) => {
                        const active = source.id === activeSourceId;
                        const ready = schemasBySource[source.id]?.columns?.length;
                        const pending = source.kind !== 'join' && source.spreadsheet_id && !ready;
                        return (
                            <button
                                key={source.id}
                                type="button"
                                onClick={() => onSelectSource(source.id)}
                                style={{
                                    ...ui.railChip,
                                    backgroundColor: active ? ink.accentSoft : ink.surface,
                                    borderColor: pending ? '#f9ab00' : (active || ready ? ink.accent : ink.lineStrong),
                                    color: active || ready ? ink.accentText : ink.muted,
                                }}
                            >
                                <span style={ui.railKind}>{source.kind === 'join' ? 'Unión' : 'Hoja'}</span>
                                <span style={ui.railName}>{sourceLabel(source)}</span>
                            </button>
                        );
                    })}
                    {activeSource ? (
                        <button
                            type="button"
                            style={ui.pillDanger}
                            onClick={() => (activeSource.kind === 'join'
                                ? onRemoveJoin(activeSource.joinId)
                                : onRemoveSource(activeSource.id))}
                        >
                            {activeSource.kind === 'join' ? 'Quitar unión' : 'Quitar'}
                        </button>
                    ) : null}
                </aside>

                <div style={ui.main}>
                    {!activeSource ? (
                        <p style={ui.empty}>Añade una hoja o elige una cápsula del catálogo.</p>
                    ) : activeSource.kind === 'join' ? (
                        <div style={ui.card}>
                            <span style={ui.cardKicker}>Unión</span>
                            <label style={ui.field}>
                                <span style={ui.label}>Nombre</span>
                                <input
                                    style={ui.input}
                                    value={activeSource.label || ''}
                                    onChange={(e) => onPatchSource(activeSource.id, { label: e.target.value })}
                                    placeholder="Clientes ⋈ Pedidos"
                                />
                            </label>
                            <p style={ui.hint}>Claves y tipo: en el mapa de la derecha.</p>
                        </div>
                    ) : (
                        <div style={ui.card}>
                            <div style={ui.cardHead}>
                                <span style={ui.cardKicker}>Conexión</span>
                                <span style={{
                                    ...ui.status,
                                    backgroundColor: cols ? ink.accentSoft : (waiting ? '#fff8e1' : ink.wash),
                                    color: cols ? ink.accentText : (waiting ? '#e37400' : ink.faint),
                                }}>
                                    {loadingSheets || loadingColumns
                                        ? 'Conectando…'
                                        : (cols
                                            ? `${cols} cols${rows != null ? ` · ${Number(rows).toLocaleString('es-MX')} filas` : ''}`
                                            : (spreadsheetId ? 'Elige pestaña' : 'Sin conectar'))}
                                </span>
                            </div>
                            <div style={ui.inline}>
                                <input
                                    style={{ ...ui.input, flex: '0 1 120px' }}
                                    value={activeSource.label || ''}
                                    onChange={(e) => onPatchSource(activeSource.id, { label: e.target.value })}
                                    placeholder="Nombre"
                                    title="Nombre de la fuente"
                                />
                                <select
                                    value={sheetName}
                                    onChange={(e) => {
                                        /* Una fuente que ya usan elementos cambia de pestaña desde «Reemplazar fuente». */
                                        if (sourceInUse && onReplaceSource) onReplaceSource(activeSource.id, undefined, e.target.value);
                                        else onSheetChange(e.target.value);
                                    }}
                                    style={{ ...ui.input, flex: '0 1 140px' }}
                                    disabled={availableSheets.length === 0}
                                    title="Pestaña"
                                >
                                    <option value="">{availableSheets.length ? 'Pestaña…' : 'Carga el archivo'}</option>
                                    {availableSheets.map((item) => (
                                        <option key={`${item.gid}-${item.title}`} value={item.title}>{item.title}</option>
                                    ))}
                                </select>
                                <input
                                    type="text"
                                    placeholder="URL o ID de Sheets"
                                    value={spreadsheetInput}
                                    onChange={(e) => onPatchSource(activeSource.id, { input: e.target.value })}
                                    onBlur={() => {
                                        const parsed = parseSpreadsheetInput(spreadsheetInput);
                                        if (!parsed.spreadsheet_id || !activeSource || sourceInUse) return;
                                        if (parsed.spreadsheet_id !== spreadsheetId || !availableSheets.length) {
                                            onLoadWorksheets(spreadsheetInput, null, activeSource.id);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            connect();
                                        }
                                    }}
                                    style={{ ...ui.input, flex: '1 1 160px' }}
                                    title="URL o ID de Google Sheets"
                                />
                                <button
                                    type="button"
                                    style={ui.pillGhost}
                                    onClick={sourceInUse && onReplaceSource ? () => onReplaceSource(activeSource.id) : onOpenDrive}
                                    title="Elegir en Drive"
                                >
                                    Drive
                                </button>
                                {sourceInUse && onReplaceSource && (
                                    <button
                                        type="button"
                                        style={ui.pillGhost}
                                        onClick={() => onReplaceSource(activeSource.id)}
                                        title="Cambiar archivo, enlace o pestaña sin perder los elementos del reporte"
                                    >
                                        Reemplazar…
                                    </button>
                                )}
                                <button
                                    type="button"
                                    style={{
                                        ...ui.pillAccent,
                                        opacity: (loadingSheets || loadingColumns) ? 0.55 : 1,
                                    }}
                                    disabled={loadingSheets || loadingColumns}
                                    onClick={connect}
                                >
                                    {loadingSheets ? 'Listando…' : (loadingColumns ? 'Leyendo…' : 'Conectar')}
                                </button>
                            </div>
                            <label style={ui.field}>
                                <span style={ui.label}>Formato de fecha</span>
                                <select
                                    value={normalizeDateOrder(activeSource.dateOrder)}
                                    onChange={(e) => onPatchSource(activeSource.id, { dateOrder: e.target.value })}
                                    style={{ ...ui.input, width: 200, maxWidth: '100%', alignSelf: 'flex-start' }}
                                    title="Cómo leer fechas como 05/03/2026. En automático se detecta por columna y, si es ambigua, se toma día/mes."
                                >
                                    {DATE_ORDER_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    )}

                    {activeSource ? (
                        <>
                            <div style={ui.toolCard}>
                                <SourceFiltersPanel
                                    compact
                                    source={activeSource}
                                    columns={schema.columns || schemaColumns}
                                    dataSources={dataSources}
                                    joins={joins}
                                    onChange={onSourceFiltersChange}
                                />
                            </div>
                            <div style={ui.toolCard}>
                                <SourceColumnPicker
                                    source={activeSource}
                                    columns={schema.columns || schemaColumns}
                                    onOpen={() => setFieldsOpen(true)}
                                />
                            </div>
                        </>
                    ) : null}
                </div>

                <div style={ui.mapCard}>
                    <span style={ui.cardKicker}>Mapa de uniones</span>
                    <JoinBlendCanvas
                        dataSources={dataSources}
                        joins={joins}
                        schemasBySource={schemasBySource}
                        activeSourceId={activeSourceId}
                        loadingColumns={loadingColumns}
                        onSelectSource={onSelectSource}
                        onBlend={onBlend}
                        onPatchJoin={onPatchJoin}
                        onTestJoin={onTestJoin}
                        onRemoveJoin={onRemoveJoin}
                        showInspector={false}
                        onExpand={onOpenJoinMap}
                    />
                </div>
            </div>
            <SourceFieldsModal
                open={fieldsOpen}
                onClose={() => setFieldsOpen(false)}
                dataSources={dataSources}
                joins={joins}
                schemasBySource={schemasBySource}
                activeSourceId={activeSourceId}
                onSelectSource={onSelectSource}
                onChange={onSourceColumnsChange}
            />
        </section>
    );
}

const ui = {
    shell: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        flexShrink: 0,
        height: '48vh',
        minHeight: 360,
        maxHeight: '58vh',
        overflow: 'hidden',
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 16,
        padding: '10px 12px 12px',
        boxSizing: 'border-box',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12,
        flexWrap: 'wrap',
        flexShrink: 0,
    },
    sub: {
        margin: '4px 0 0',
        fontSize: '0.72rem',
        color: ink.muted,
        lineHeight: 1.4,
        maxWidth: 420,
    },
    headerActions: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' },
    body: {
        display: 'grid',
        gridTemplateColumns: 'minmax(128px, 156px) minmax(360px, 440px) minmax(0, 1fr)',
        gap: 10,
        alignItems: 'stretch',
        minHeight: 0,
        flex: 1,
        overflow: 'hidden',
    },
    rail: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        minWidth: 0,
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
    },
    railLabel: {
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: ink.faint,
        paddingLeft: 4,
    },
    railChip: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2,
        width: '100%',
        textAlign: 'left',
        padding: '7px 12px 8px',
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        cursor: 'pointer',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
    },
    railKind: {
        fontSize: '0.6rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        opacity: 0.75,
    },
    railName: {
        fontSize: '0.78rem',
        fontWeight: 650,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
    },
    main: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minWidth: 0,
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
    },
    card: {
        backgroundColor: ink.wash,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 14,
        padding: '8px 10px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        flexShrink: 0,
    },
    toolCard: {
        backgroundColor: ink.wash,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 14,
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        flexShrink: 0,
    },
    mapCard: {
        backgroundColor: ink.wash,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 14,
        padding: '8px 10px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
    },
    cardHead: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
    },
    cardKicker: {
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: ink.accent,
    },
    status: {
        fontSize: '0.68rem',
        fontWeight: 700,
        borderRadius: 999,
        padding: '3px 10px',
        whiteSpace: 'nowrap',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        gap: 8,
    },
    inline: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        alignItems: 'center',
    },
    field: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
    label: {
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: ink.faint,
    },
    input: {
        padding: '4px 12px',
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        fontSize: '0.78rem',
        minHeight: 30,
        height: 30,
        fontFamily: 'inherit',
        color: ink.text,
        boxSizing: 'border-box',
        width: '100%',
    },
    urlRow: { display: 'flex', gap: 6, alignItems: 'center', minWidth: 0 },
    hint: { margin: 0, fontSize: '0.72rem', color: ink.muted, lineHeight: 1.4 },
    empty: { margin: 0, fontSize: '0.78rem', color: ink.faint },
    pillGhost: {
        appearance: 'none',
        padding: '4px 12px',
        minHeight: 30,
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        color: ink.accentText,
        cursor: 'pointer',
        fontSize: '0.72rem',
        fontWeight: 700,
        fontFamily: 'inherit',
        whiteSpace: 'nowrap',
    },
    pillAccent: {
        appearance: 'none',
        padding: '4px 14px',
        minHeight: 30,
        borderRadius: 999,
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: ink.accent,
        color: ink.surface,
        cursor: 'pointer',
        fontSize: '0.72rem',
        fontWeight: 700,
        fontFamily: 'inherit',
        whiteSpace: 'nowrap',
    },
    pillQuiet: {
        appearance: 'none',
        padding: '4px 12px',
        minHeight: 30,
        borderRadius: 999,
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        color: ink.muted,
        cursor: 'pointer',
        fontSize: '0.72rem',
        fontWeight: 700,
        fontFamily: 'inherit',
    },
    pillDanger: {
        appearance: 'none',
        marginTop: 4,
        padding: '4px 12px',
        minHeight: 28,
        borderRadius: 999,
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: ink.dangerSoft,
        color: ink.danger,
        cursor: 'pointer',
        fontSize: '0.7rem',
        fontWeight: 700,
        fontFamily: 'inherit',
        alignSelf: 'flex-start',
    },
    pillDangerHead: {
        appearance: 'none',
        padding: '4px 12px',
        minHeight: 30,
        borderRadius: 999,
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: ink.dangerSoft,
        color: ink.danger,
        cursor: 'pointer',
        fontSize: '0.72rem',
        fontWeight: 700,
        fontFamily: 'inherit',
    },
};
